import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { BAND_CELL, BandChangeChip, DumbbellAxis, DumbbellLegend, DumbbellTrack, PURPLE, TEAL, bandOf, sgap } from "@/components/Dumbbell";

const SERIES = [
  { key: "school", label: "School", shape: "circle", color: TEAL },
  { key: "portfolio", label: "Portfolio", shape: "diamond", color: PURPLE },
];
const SORTS = {
  gap: (a, b) => Math.abs(b.gap ?? -1) - Math.abs(a.gap ?? -1) || a.order - b.order,
  order: (a, b) => a.order - b.order,
  portfolio: (a, b) => (a.portfolio_score ?? 999) - (b.portfolio_score ?? 999) || a.order - b.order,
};

export const takeaway = (pillars) => {
  const rows = pillars.filter((p) => p.gap != null);
  const lower = rows.filter((p) => p.gap < 0).length, higher = rows.filter((p) => p.gap > 0).length;
  const changed = rows.filter((p) => bandOf(p.school_score) !== bandOf(p.portfolio_score)).length;
  const parts = [];
  if (lower) parts.push(`You rate ${lower} of ${rows.length} pillars lower than the school`);
  if (higher) parts.push(`${lower ? "" : "You rate "}${higher} of ${rows.length} higher`);
  if (!parts.length) parts.push(`You and the school rate all ${rows.length} pillars the same`);
  return `${parts.join(" and ")} · ${changed} pillar${changed === 1 ? "" : "s"} change band`;
};

const Fmt = (v) => (v == null ? "—" : Number.isInteger(v) ? v : v.toFixed(1));
const Cell = ({ v }) => { const b = bandOf(v); return <td className={`px-3 py-2 text-[13px] font-semibold ${b ? BAND_CELL[b] : ""}`}>{Fmt(v)}{b ? ` · ${b}` : ""}</td>; };

const Row = ({ p, open, onToggle, provisional }) => {
  const changed = bandOf(p.school_score) && bandOf(p.portfolio_score) && bandOf(p.school_score) !== bandOf(p.portfolio_score);
  const sr = `${p.title}: school ${Fmt(p.school_score)} ${bandOf(p.school_score) || ""}, Portfolio ${Fmt(p.portfolio_score)} ${bandOf(p.portfolio_score) || ""}, gap ${sgap(p.gap)}`;
  return (
    <li className={changed ? "bg-[#fffbeb]" : ""} data-testid={`compare-pillar-${p.pillar_id}`}>
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`cmp-ind-${p.pillar_id}`} data-testid={`compare-row-toggle-${p.pillar_id}`}
        className="grid w-full grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 px-3 py-2 text-left hover:bg-black/[0.02] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#007a9e] min-[701px]:grid-cols-[200px_1fr_150px_20px]">
        <span className="sr-only">{sr}</span>
        <span className="text-[14px] font-semibold text-[#111827]" aria-hidden>{p.title}</span>
        <span className="col-span-2 row-start-2 min-[701px]:col-span-1 min-[701px]:row-start-1 min-[701px]:col-start-2"><DumbbellTrack values={{ school: p.school_score, portfolio: p.portfolio_score }} series={SERIES} refKey="school" provisional={provisional} /></span>
        <span className="flex flex-wrap items-center justify-end gap-1.5 text-right min-[701px]:flex-col min-[701px]:items-end" aria-hidden>
          <span className={`text-[15px] font-bold ${p.gap != null && Math.abs(p.gap) >= 15 ? "text-[#b91c1c]" : p.gap != null && Math.abs(p.gap) >= 5 ? "text-[#c2410c]" : "text-[#374151]"}`} data-testid={`compare-gap-${p.pillar_id}`}>{sgap(p.gap)}</span>
          <BandChangeChip from={p.school_score} to={p.portfolio_score} testid={`compare-band-change-${p.pillar_id}`} />
        </span>
        <span className="hidden text-[#6b7280] min-[701px]:block" aria-hidden>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
      </button>
      {open && (
        <div id={`cmp-ind-${p.pillar_id}`} className="border-t border-[#f3f4f6] bg-[#fafafa] px-4 py-3" data-testid={`compare-row-detail-${p.pillar_id}`}>
          <div className="text-[13px] font-bold text-[#111827]">Indicators with the biggest differences</div>
          {(p.top_diffs || []).length ? (
            <ul className="mt-1.5 space-y-1">
              {p.top_diffs.map((d) => (
                <li key={d.indicator_id} className="flex flex-wrap justify-between gap-2 text-[13px] text-[#374151]">
                  <span>{d.name}</span><span className="whitespace-nowrap">School {Fmt(d.school)} · Portfolio {Fmt(d.portfolio)} ({sgap(d.diff)})</span>
                </li>
              ))}
            </ul>
          ) : <p className="mt-1 text-[13px] text-[#4b5563]">No indicator-level differences.</p>}
        </div>
      )}
    </li>
  );
};

// Stage 6: School vs Portfolio per-pillar dumbbell chart with sort, Chart/Table toggle and row expand.
export const ComparePillars = ({ pillars, provisional }) => {
  const [sort, setSort] = useState("gap");
  const [mode, setMode] = useState("chart");
  const [open, setOpen] = useState(null);
  const rows = useMemo(() => pillars.map((p, i) => ({ order: p.order ?? i, ...p })).sort(SORTS[sort]), [pillars, sort]);
  const seg = (on) => `min-h-[40px] px-3 text-[13px] font-semibold ${on ? "bg-[#111827] text-white" : "bg-white text-[#111827] hover:bg-[#f3f4f6]"}`;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4" data-testid="compare-pillars">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[15px] font-bold text-[#111827]">Pillars</h2>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="cmp-sort" className="text-[13px] text-[#374151]">Sort</label>
          <select id="cmp-sort" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="compare-sort" className="min-h-[40px] rounded-md border border-[#d1d5db] px-2 text-[13px]">
            <option value="gap">Largest gap</option><option value="order">Pillar order</option><option value="portfolio">Lowest Portfolio score</option>
          </select>
          <div className="inline-flex overflow-hidden rounded-md border border-[#d1d5db]" role="group" aria-label="View">
            <button type="button" aria-pressed={mode === "chart"} className={seg(mode === "chart")} onClick={() => setMode("chart")} data-testid="compare-mode-chart">Chart</button>
            <button type="button" aria-pressed={mode === "table"} className={seg(mode === "table")} onClick={() => setMode("table")} data-testid="compare-mode-table">Table</button>
          </div>
        </div>
      </div>
      <div className="mt-3"><DumbbellLegend series={SERIES} /></div>
      {mode === "chart" ? (
        <>
          <div className="mt-3 hidden grid-cols-[200px_1fr_150px_20px] gap-x-3 px-3 min-[701px]:grid"><span /><DumbbellAxis /><span className="text-right text-[12px] text-[#4b5563]">Gap</span><span /></div>
          <ul className="mt-1 divide-y divide-slate-100 border-t border-slate-100">
            {rows.map((p) => <Row key={p.pillar_id} p={p} provisional={provisional} open={open === p.pillar_id} onToggle={() => setOpen(open === p.pillar_id ? null : p.pillar_id)} />)}
          </ul>
          <p className="mt-2 text-[12.5px] text-[#4b5563]">Select a pillar to see the indicators with the biggest differences.</p>
        </>
      ) : (
        <div className="mt-3 overflow-x-auto" data-testid="compare-table">
          <table className="w-full min-w-[620px] border-collapse text-left">
            <thead><tr className="bg-[#f3f4f6] text-[12px] text-[#374151]">{["Pillar", "School", "Portfolio", "Gap", "Band change"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.pillar_id} className="border-t border-[#e5e7eb]">
                  <th scope="row" className="sticky left-0 bg-white px-3 py-2 text-[13px] font-semibold text-[#111827]">{p.title}</th>
                  <Cell v={p.school_score} /><Cell v={p.portfolio_score} />
                  <td className="px-3 py-2 text-[13px] font-bold">{sgap(p.gap)}</td>
                  <td className="px-3 py-2"><BandChangeChip from={p.school_score} to={p.portfolio_score} />{bandOf(p.school_score) === bandOf(p.portfolio_score) && "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ComparePillars;
